import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { TerminalLockMode } from '../../lib/terminalLock';
import type { ActiveEmergency } from '../../lib/emergencyLocation';
import { clearTerminalLockStorage, persistTerminalLock, readTerminalLockFromStorage } from '../../lib/terminalLock';
import { logout } from './authSlice';

type ViewMode = 'desktop' | 'mobile';
type SidebarPanel = 'incidents' | 'resources' | 'personnel' | 'map' | 'none';

interface UiState {
  viewMode: ViewMode;
  sidebarOpen: boolean;
  sidebarPanel: SidebarPanel;
  commandPaletteOpen: boolean;
  rightPanelOpen: boolean;
  mapFullscreen: boolean;
  theme: 'dark' | 'light';
  mobileTab: 'dispatch' | 'incidents' | 'map' | 'profile';
  emergencyLocation: ActiveEmergency | null;
  terminalLocked: boolean;
  terminalLockMode: TerminalLockMode | null;
  terminalReturnPath: string | null;
}

const storedLock = readTerminalLockFromStorage();

const initialState: UiState = {
  viewMode: window.innerWidth >= 1024 ? 'desktop' : 'mobile',
  sidebarOpen: true,
  sidebarPanel: 'incidents',
  commandPaletteOpen: false,
  rightPanelOpen: true,
  mapFullscreen: false,
  theme: 'dark',
  mobileTab: 'dispatch',
  emergencyLocation: null,
  terminalLocked: storedLock.locked,
  terminalLockMode: storedLock.mode,
  terminalReturnPath: storedLock.returnPath,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setViewMode(state, action: PayloadAction<ViewMode>) {
      state.viewMode = action.payload;
    },
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarPanel(state, action: PayloadAction<SidebarPanel>) {
      state.sidebarPanel = action.payload;
      state.sidebarOpen = true;
    },
    toggleCommandPalette(state) {
      state.commandPaletteOpen = !state.commandPaletteOpen;
    },
    toggleRightPanel(state) {
      state.rightPanelOpen = !state.rightPanelOpen;
    },
    toggleMapFullscreen(state) {
      state.mapFullscreen = !state.mapFullscreen;
    },
    setTheme(state, action: PayloadAction<'dark' | 'light'>) {
      state.theme = action.payload;
    },
    setMobileTab(state, action: PayloadAction<UiState['mobileTab']>) {
      state.mobileTab = action.payload;
    },
    setEmergencyLocation(state, action: PayloadAction<ActiveEmergency | null>) {
      state.emergencyLocation = action.payload;
    },
    lockTerminal(state, action: PayloadAction<{ returnPath: string; lockedUserId: string }>) {
      state.terminalLocked = true;
      state.terminalLockMode = 'lock';
      state.terminalReturnPath = action.payload.returnPath;
      persistTerminalLock('lock', action.payload.returnPath, action.payload.lockedUserId);
    },
    switchUserTerminal(state, action: PayloadAction<{ returnPath: string }>) {
      state.terminalLocked = true;
      state.terminalLockMode = 'switch';
      state.terminalReturnPath = action.payload.returnPath;
      persistTerminalLock('switch', action.payload.returnPath);
    },
    setTerminalSwitchMode(state) {
      state.terminalLockMode = 'switch';
      if (state.terminalReturnPath) {
        persistTerminalLock('switch', state.terminalReturnPath);
      }
    },
    unlockTerminal(state) {
      state.terminalLocked = false;
      state.terminalLockMode = null;
      state.terminalReturnPath = null;
      clearTerminalLockStorage();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.terminalLocked = false;
      state.terminalLockMode = null;
      state.terminalReturnPath = null;
      state.emergencyLocation = null;
    });
  },
});

export const {
  setViewMode,
  toggleSidebar,
  setSidebarPanel,
  toggleCommandPalette,
  toggleRightPanel,
  toggleMapFullscreen,
  setTheme,
  setMobileTab,
  setEmergencyLocation,
  lockTerminal,
  switchUserTerminal,
  setTerminalSwitchMode,
  unlockTerminal,
} = uiSlice.actions;
export default uiSlice.reducer;
